import { useState, useCallback, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface MapOverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function MapOverlay({ open, onClose, children }: MapOverlayProps) {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (open && !hasOpened) setHasOpened(true);
  }, [open, hasOpened]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 150);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, handleClose]);

  if (!hasOpened) return null;

  const visible = open || closing;

  return (
    <div
      className={`cx-map-overlay ${closing ? "cx-map-overlay--closing" : ""}`}
      style={visible ? undefined : { visibility: "hidden", pointerEvents: "none" }}
      role="dialog"
      aria-label={t("nav.showMap")}
      aria-hidden={!visible}
    >
      {visible && (
        <button
          className="cx-map-overlay-back"
          onClick={handleClose}
          aria-label={t("nav.back")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      <div style={{ position: "absolute", inset: 0 }}>
        {children}
      </div>
    </div>
  );
}
